// DS5.5, DS5.5.1
import React, { useContext, useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';
import EmptyWatchedGroups from './_partials/EmptyWatchedGroups';
import NetworkBarLink from './_partials/NetworkBarLink';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import CoreIcon from '@wix/da-ds/pkg/Icons/24x24/Core';
import HomeIcon from '@wix/da-ds/pkg/Icons/24x24/Home';
import Eyecon from '@wix/da-ds/pkg/Icons/24x24/FollowingWatch';
import ShopIcon from '@wix/da-ds/pkg/Icons/24x24/Shop';
import DailyDeviationIcon from '@wix/da-ds/pkg/Icons/24x24/DailyDeviation';
import TopicsIcon from '@wix/da-ds/pkg/Icons/24x24/Topics';
import NewestIcon from '@wix/da-ds/pkg/Icons/24x24/Newest';
import DreamUpIcon from '@wix/da-ds/pkg/Icons/24x24/DreamUp';
import RecommendedIcon from '@wix/da-ds/pkg/Icons/Recommended';
import LightningIcon from '@wix/da-ds/pkg/Icons/24x24/Lightning';
import VideoCreatorIcon from '@wix/da-ds/pkg/Icons/VideoCreator';
import HelpCenterIcon from '@wix/da-ds/pkg/Icons/24x24/HelpCenter';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import AddToGroupFill from '@wix/da-ds/pkg/Icons/16x16/AddToGroupFill';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { Nav } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import {
  watchedGroupsStreamId,
  recommendedGroupsStreamId,
} from './redux/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { MainNavigationItem } from './redux/types';
import { useScrollDescendantIntoView } from './hooks/useScrollDescendantIntoView';
import FoldableGroupList from './_partials/FoldableGroupList';
import NetworkBarItemGroup from './_partials/NetworkBarItemGroup';
import SiteHeaderPlacement from '../DealerAd/Placement/SiteHeader';
import { BiData, Networkbar3DotsSelectBiEvent } from '@wix/da-bi/pkg/events';

import s from './NetworkBar.scss';

const menuItemIconMap = {
  home: HomeIcon,
  watch: Eyecon,
  shop: ShopIcon,
  dailyDeviations: DailyDeviationIcon,
  topics: TopicsIcon,
  new: NewestIcon,
  dreamup: DreamUpIcon,
  dailyChallenges: LightningIcon,
  dreamupAnon: DreamUpIcon,
  videoCreator: VideoCreatorIcon,
  helpCenter: HelpCenterIcon,
};

export interface Props {
  /** Note: these are configured in NetworkBar/redux/helpers, getMainNavigationItems */
  mainNavItems: MainNavigationItem[];
  id?: string;
  isVisible?: boolean;
  isCollapsed?: boolean;
  selectedItem?: string | number;
  showWatchedGroupsEmptyState: boolean;
  showRecommendedGroups: boolean;
  className?: string;
  isInitialized: boolean;
  canSeeOldWatch?: boolean;
  canSeeShop?: boolean;
  initialFetch: () => void;
}

function useFilteredMainNavigationItems(items: MainNavigationItem[]) {
  const isMobile = useContext(MobileContext);
  const { isLoggedIn, aiContentFilter } = useContext(SessionContext);
  const features = useEnabledFlagList();

  return items.filter(item => {
    if (!item || item.key === 'new') {
      return false;
    }

    if (
      (item.restrictLoggedIn && !isLoggedIn) ||
      (item.anonOnly && isLoggedIn)
    ) {
      return false;
    }

    if (
      (item.desktopOrMobileOnly === 'desktop' && isMobile) ||
      (item.desktopOrMobileOnly === 'mobile' && !isMobile)
    ) {
      return false;
    }

    if (item.hideIfAISuppressed && aiContentFilter === 'suppress') {
      return false;
    }

    if (
      (item.withFeature && !features.includes(item.withFeature)) ||
      (item.hideWithFeature && features.includes(item.hideWithFeature))
    ) {
      return false;
    }

    return true;
  });
}

const NetworkBar: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  initialFetch,
  isVisible = true,
  isCollapsed,
  selectedItem,
  showWatchedGroupsEmptyState,
  showRecommendedGroups,
  className,
  isInitialized,
  mainNavItems,
  canSeeOldWatch,
}) => {
  const [hideContent, setHideContent] = useState(true);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { isLoggedIn, hasCoreMembership } = useContext(SessionContext);
  const tabIndex = isVisible ? 0 : -1;

  const mainNavItemsFiltered = useFilteredMainNavigationItems(mainNavItems);

  const createGroupLink = (
    <NetworkBarLink
      label="groupsCreate"
      url={Url.to(URLS.GROUPS_CREATE)}
      iconElement={
        <span
          className={classnames(
            s['create-group'],
            isCollapsed && s['create-group-collapsed']
          )}
        >
          <AddIcon disableDefaultColors size={IconSize.SMALLER} />
        </span>
      }
      tabIndex={tabIndex}
      isCollapsed={!isMobile && isCollapsed}
      showTooltip
    />
  );

  useScrollDescendantIntoView(scrollableRef, s['selected-item'], isInitialized);

  useEffect(() => {
    if (!isInitialized && isVisible) {
      initialFetch();
    }
    setHideContent(false);
  }, [initialFetch, isInitialized, isVisible]);

  return (
    <BiLoggerContextProvider value={{ is_expanded: !isCollapsed ? 1 : 0 }}>
      <div
        className={classnames(
          !isMobile && s['scroll-wrapper'],
          isCollapsed && s['collapsed'],
          hideContent && s['hidden'],
          className
        )}
        ref={scrollableRef}
      >
        <Nav className={s['root']} id={id}>
          <VisuallyHidden>
            <Heading>{t('common.networkBar')}</Heading>
          </VisuallyHidden>
          {isMobile && (
            <>
              <SiteHeaderPlacement className={s['dealer-ad']} />
            </>
          )}
          <ul>
            {isMobile && !hasCoreMembership && (
              <li>
                <NetworkBarLink
                  label="mobileCore"
                  url={Url.coreMembershipLink()}
                  icon={CoreIcon}
                  tabIndex={tabIndex}
                  // Doubling up so that we can change the color of the text and the icon
                  linkClassName={s['core-item']}
                  iconClassName={s['core-item']}
                />
              </li>
            )}

            {mainNavItemsFiltered.map(item => {
              return (
                <li key={item.key}>
                  <NetworkBarLink
                    label={item.key}
                    url={item.url}
                    icon={menuItemIconMap[item.key]}
                    tabIndex={tabIndex}
                    isCollapsed={!isMobile && isCollapsed}
                    isSelected={selectedItem === (item.pageType ?? item.key)}
                    className={classnames(
                      selectedItem === (item.pageType ?? item.key) &&
                        s['selected-item']
                    )}
                    hasNewContent={item.hasNewContent}
                    betaBadge={item.betaBadge}
                    showTooltip
                    menuItems={
                      isMobile && item.key === 'watch' && canSeeOldWatch
                        ? [
                            {
                              label: t(
                                'networkBar.navItems.watch.actions.oldWatch'
                              ),
                              link: Url.watchLink(),
                              biData: BiData<Networkbar3DotsSelectBiEvent>({
                                evid: 163,
                                sectionname: item.key,
                                indicator: item.hasNewContent ? 1 : 0,
                                menu_item: 'oldWatch',
                              }),
                            },
                          ]
                        : undefined
                    }
                  />
                </li>
              );
            })}
            {showWatchedGroupsEmptyState && isCollapsed && isLoggedIn && (
              <li>{createGroupLink}</li>
            )}
          </ul>
          {showWatchedGroupsEmptyState && !isCollapsed && (
            <div className={s['group']}>
              <BiLoggerContextProvider value={{ sectionname: 'groups' }}>
                <NetworkBarItemGroup
                  title={t('networkBar.subheader.groups')}
                  icon={AddToGroupFill}
                  isCollapsed={isCollapsed}
                >
                  {isLoggedIn && createGroupLink}
                  <EmptyWatchedGroups />
                </NetworkBarItemGroup>
              </BiLoggerContextProvider>
            </div>
          )}
          {!showWatchedGroupsEmptyState && isLoggedIn && (
            <BiLoggerContextProvider value={{ sectionname: 'groups' }}>
              <FoldableGroupList
                sectionType="watched_groups"
                streamId={watchedGroupsStreamId}
                tabIndex={tabIndex}
                selectedItem={selectedItem}
                isCollapsed={isCollapsed}
                selectedItemClassName={s['selected-item']}
                icon={AddToGroupFill}
                title={t('networkBar.subheader.groups')}
                toggleAriaLabel={t('networkBar.subheader.groups')}
                prefix={createGroupLink}
                className={s['group']}
              />
            </BiLoggerContextProvider>
          )}
          {showRecommendedGroups && (
            <BiLoggerContextProvider
              value={{ sectionname: 'recommended groups' }}
            >
              <FoldableGroupList
                sectionType="recommended_groups"
                streamId={recommendedGroupsStreamId}
                tabIndex={tabIndex}
                selectedItem={selectedItem}
                isCollapsed={isCollapsed}
                selectedItemClassName={s['selected-item']}
                className={s['group']}
                icon={RecommendedIcon}
                title={t('networkBar.subheader.recommendedGroups')}
                toggleAriaLabel={t('networkBar.subheader.recommendedGroups')}
              />
            </BiLoggerContextProvider>
          )}
        </Nav>
      </div>
    </BiLoggerContextProvider>
  );
};
NetworkBar.displayName = 'NetworkBar';

export default NetworkBar;
